import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

/**
 * Calls a function when a certain key on the keyboard is pressed.
 */
export function useKeyPress(
    key: string | string[],
    callback: ((event: KeyboardEvent) => void) | undefined
) {
    // The "latest ref" pattern, used to ensure that we always have the latest callback,
    // but don't rerender unnecessarily when the callback changes.
    const callbackRef = useRef(callback);
    useLayoutEffect(() => {
        callbackRef.current = callback;
    });

    const handler = useMemo(
        () =>
            typeof key === 'string'
                ? (event: KeyboardEvent) => {
                      if (event.key === key) {
                          callbackRef.current?.(event);
                      }
                  }
                : (event: KeyboardEvent) => {
                      if (key.includes(event.key)) {
                          callbackRef.current?.(event);
                      }
                  },
        [key]
    );

    useEffect(() => {
        window.addEventListener('keydown', handler);
        return () => {
            window.removeEventListener('keydown', handler);
        };
    }, [handler]);
}
